/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import guidebookContentStyle from "../guidebookContentStyle";
import TextContainer from "../../Container/TextContainer";
import Layout from "../../../containers/Layouts/Layout";
import TeaserSlogan from "../../Teaser/TeaserSlogan";
import QuestionsContactSection from "../QuestionsContactSection/QuestionsContactSection";
import DoubleButton from "../../Button/DoubleButton";
import { Helmet } from "react-helmet-async";
import HighlightBox from "../../Box/HighlightBox/HighlightBox";
import backgroundTeaser from "../../../assets/img/guidebook/Teaser_Abfindung-Ablauf_700px.jpg";
import Typography from "@mui/material/Typography";
import { Paper } from "@mui/material";
import { gaSet } from "../../../services/googleAnalyticsService";

const GuidebookSettlementProcedure = () => {
  const title = "Ratgeber - Alles zum Ablauf nach einer Kündigung | Legalbird";

  gaSet({ title: title });

  return (
    <Layout backgroundType={"serviceworld"}>
      <Helmet>
        <title>{title}</title>
        <meta
          name="description"
          content="Wenn Ihnen gekündigt wurde, haben Sie die Möglichkeit, gegen die Kündigung zu klagen, um zum Beispiel eine
            Abfindung zu erhalten. Wir erklären Ihnen den Ablauf."
        />
      </Helmet>

      <Paper sx={guidebookContentStyle.paper}>
        <TeaserSlogan slogan={"Alles zum Ablauf nach einer Kündigung"} backgroundImagePath={backgroundTeaser} height={300} headingColor={"#333333"} />
        <TextContainer>
          <p>
            Wenn Ihnen gekündigt wurde, haben Sie die Möglichkeit, gegen die Kündigung zu klagen, um zum Beispiel eine Abfindung zu erhalten. Wie Sie sich jetzt
            verhalten sollten und was Sie tun müssen, damit Sie das bestmögliche Ergebnis erzielen, erklären wir Ihnen hier.
          </p>
          <hr />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Schnell und professionell handeln
          </Typography>
          <p>
            Das Wichtigste zuerst: Überlegen Sie in Ruhe, wie Sie mit der neuen Situation umgehen wollen. Wenn Sie unbedingt auf Ihrer Arbeitsstelle bleiben
            wollen, müssen Sie anders vorgehen, als wenn Sie Ihren Job ohnehin abgeschrieben haben und eine möglichst hohe Abfindung erzielen wollen.
          </p>
          <p>
            Als Arbeitnehmer haben Sie das Recht, eine Klage gegen die Kündigung einzureichen, um hiermit eine Abfindung zu erwirken oder wieder auf Ihren
            Arbeitsplatz zurückzukehren - je nachdem, welches Ergebnis Sie sich wünschen. Um gegen die Kündigung anzugehen, können Sie diese kostenlos von uns
            prüfen lassen. Wenn Ihre Kündigung rechtswidrig ist, können Sie eine sogenannte Kündigungsschutzklage bei Gericht einreichen. Hierbei gilt eine
            Frist von 3 Wochen. Die Frist beginnt erst mit Zugang einer schriftlichen Kündigung.
          </p>
          <HighlightBox headline={"Wie verhalte ich mich nach einer Kündigung?"}>
            <ol>
              <li>Ruhe bewahren und überlegen, welches Ziel durch eine Klage erreicht werden soll</li>
              <li>Kommunikation mit dem Arbeitgeber und Arbeitskollegen professionell weiterführen, aber möglichst nicht über die Kündigung sprechen</li>
              <li>
                Auf Legalbird die Kündigung unverbindlich prüfen Lassen. Hierzu einfach die Angaben zur Kündigung vervollständigen und den Arbeitsvertrag und das
                Kündigungsschreiben hochladen
              </li>
              <li>Erteilen Sie uns die Vollmacht, damit wir Ihre Kündigung prüfen und Sie vertreten dürfen, falls Sie Klage einreichen möchten</li>
              <li>Jegliche Kommunikation mit dem Arbeitgeber übernehmen wir ab dem Zeitpunkt der Beauftragung für Sie</li>
            </ol>
          </HighlightBox>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            1. Klage gegen Kündigung und Verhandlungen mit dem Arbeitgeber
          </Typography>
          <p>
            Um sich gegen die Kündigung zu wehren und eine Abfindung erhalten zu können, müssen Sie zunächst innerhalb der 3-Wochen-Frist Klage einreichen. In
            der sogenannten Kündigungsschutzklage sind alle Angaben zum Arbeitsverhältnis und zum Ablauf der Kündigung enthalten. Alle relevanten Angaben können
            Sie innerhalb von 5 Minuten in der Servicewelt machen. Für die Klage werden zudem der Arbeitsvertrag und das Kündigungsschreiben benötigt, die Sie
            ganz unkompliziert jederzeit mit dem Handy abfotografieren und hochladen können.
          </p>
          <p>
            Sobald Sie uns die Vollmacht erteilt haben, prüfen wir Ihre Kündigung kostenlos. Sollte die Kündigung rechtswidrig sein, erstellen wir in Absprache
            mit Ihnen die Kündigungsschutzklage und übernehmen von nun an das Mandat und jegliche Kommunikation mit dem Arbeitgeber und dem Gericht für Sie.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            2. Gütetermin
          </Typography>
          <p>
            Innerhalb von 2 Wochen nach Einreichen der Klage legt das Arbeitsgericht einen Gütetermin fest. In dieser Verhandlung versucht der vorsitzende
            Richter den Sachverhalt mit den Parteien zu erörtern und eine gütliche Einigung herbeizuführen. Beim Gütetermin müssen Sie nicht persönlich anwesend
            sein, da Ihr Anwalt Sie vertritt. Führt der Gütetermin zu keinem Vergleich, kann der vorsitzende Richter einen weiteren Gütetermin festsetzen oder -
            wenn keine Einigung abzusehen ist - einen Kammertermin festsetzen.
          </p>
          <p>
            Oftmals kann Ihr Anwalt auch bereits vor dem Gütetermin eine Einigung mit Ihrem Arbeitgeber erzielen, sodass der Gütetermin gar nicht mehr
            stattfinden muss.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            3. Kammertermin
          </Typography>
          <p>
            Im Kammertermin wird meist erneut versucht eine Einigung zwischen den Parteien zu finden. Falls keine einvernehmliche Lösung zwischen den Parteien
            zustande kommt, entscheidet das Arbeitsgericht nach der Verhandlung über die Klage. Sprich, das Gericht entscheidet darüber, ob die Kündigung
            wirksam oder unwirksam ist.
          </p>
          <p>
            Ist die Kündigung unwirksam, dem Arbeitnehmer die Fortsetzung des Arbeitsverhältnisses jedoch nicht zumutbar, muss das Gericht das Arbeitsverhältnis
            auflösen. Hierzu muss ein Antrag gestellt werden, durch den das Gericht den Arbeitgeber auch zur Zahlung einer angemessenen Abfindung verurteilen
            kann.
          </p>
          <HighlightBox headline={"Der Ablauf in Kürze"}>
            <ol>
              <li>Kündigungsschutzklage wird eingereicht</li>
              <li>Ihr Anwalt führt Verhandlungen mit dem Arbeitgeber</li>
              <li>Gericht legt Datum für Gütetermin fest</li>
              <li>Einigung mit Arbeitgeber kann bereits vor oder während des Gütetermins erzielt werden</li>
              <li>Bei fehlender Einigung wird im Kammertermin über die Wirksamkeit der Kündigung entschieden</li>
            </ol>
          </HighlightBox>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Kündigung unverbindlich prüfen lassen
          </Typography>
          <p>
            Starten Sie jetzt ganz einfach mit dem ersten Schritt und vervollständigen Sie innerhalb von 5 Minuten die Angaben zu Ihrer Kündigung. Wir prüfen
            Ihre Kündigung unverbindlich und sorgen dann dafür, dass Sie mit professioneller Unterstützung das bestmögliche Ergebnis erzielen!
          </p>
          <DoubleButton
            buttonText={"Kündigung unverbindlich prüfen"}
            buttonLink={"/servicewelt"}
            backText={"Zurück zum Ratgeber"}
            backLink={"/ratgeber/arbeitsrecht"}
          />
          <QuestionsContactSection />
        </TextContainer>
      </Paper>
    </Layout>
  );
};

export default GuidebookSettlementProcedure;
